"use client";
import QBosonBuildsTrustAnimation from "./qBosonBuildsTrust";
import CICDAnimation from "./cicdAnimation";
import DevelopmentProcessAnimation from "./developmentProcess";
import FallbackAnimation from "./fallback";
import IntegratedTestingAnimation from "./integratedTesting";
import ProductEcosystemAnimation from "./productEcosystem";
import QualitiaCustomersAnimation from "./qualitiaCustomers";
import { useEffect, useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";

gsap.registerPlugin(ScrollTrigger);

interface AnimationProps {
  data: {
    titleAnimation: string;
    isArc: boolean;
    animationName:
      | "Development_process"
      | "Q_Boson_builds_trust"
      | "Qualitia_customers"
      | "Product_ecosystem"
      | "Integrated_testing"
      | "CI_CD";
    description: string;
  };
}

export default function Animation({ data }: AnimationProps) {
  const MAP_ANIMATIONS = {
    Development_process: DevelopmentProcessAnimation,
    Q_Boson_builds_trust: QBosonBuildsTrustAnimation,
    Qualitia_customers: QualitiaCustomersAnimation,
    Product_ecosystem: ProductEcosystemAnimation,
    Integrated_testing: IntegratedTestingAnimation,
    CI_CD: CICDAnimation,
  };

  const Component = MAP_ANIMATIONS[data.animationName] || FallbackAnimation;
  const textref = useRef<HTMLDivElement>(null);

  const getCardClasses = (isCard: boolean): string => {
    return isCard
      ? "bg-white rounded-[16px] sm:rounded-[32px] pb-[30px] pt-[48px] lg:pb-[72px] lg:pt-[96px] px-6 build-box-shadow"
      : "";
  };

  const getBgClasses = (type: string): string => {
    switch (type) {
      case "CI_CD":
        return "bg-gradient-to-t from-[#DBF2FF] to-[#FFFFFF] py-[48px] xl:py-[96px] ";
      case "Qualitia_customers":
        return "bg-gradient-to-b from-[#DBF2FF] to-[#FFFFFF] py-[60px] xl:py-[120px] ";
      case "Product_ecosystem":
        return "bg-[#DBF2FF4D] py-[80px] xl:py-[120px]";
      case "Integrated_testing":
        return "bg-[#DBF2FF4D] py-[48px] xl:py-[96px] height-100";
      case "Development_process":
        return "bg-white pt-[48px] xl:pt-[96px]";
      case "Q_Boson_builds_trust":
        return "bg-[#DBF2FF]";
      default:
        return "bg-white py-[48px] xl:py-[96px] ";
    }
  };
  const getTitleClasses = (type: string): string => {
    switch (type) {
      case "Q_Boson_builds_trust":
        return "mt-0 mb-[40px] lg:w-7/12 2xl:w-8/12 text-center ";
      case "Product_ecosystem":
        return "mb-[48px] md:mb-[80px] text-center ";
      case "Qualitia_customers":
        return "mb-[25px] md:mb-[50px] text-center ";
      default:
        return "mb-[16px] text-center ";
    }
  };

  useLayoutEffect(() => {
    if (!textref.current) return;
  
    if (data && textref.current) {
      const title = textref.current.querySelectorAll(".animation-title");
      const description = textref.current.querySelectorAll(".description");

      const tl = gsap.timeline({ defaults: { ease: "power3.inOut" } });

      tl.fromTo(
        title,
        { y: 200, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "none" }
      );
      {description.length && tl.fromTo(
        description,
        { opacity: 0 },
        { opacity: 1, duration: 1, ease: "none" }
      );}

      ScrollTrigger.create({
        trigger: textref.current,
        start: "top 80%",
        end: "bottom 10%",
        animation: tl,
        toggleActions: "restart none none reverse",
        onEnter: () => tl.play(),
        onLeaveBack: () => tl.reverse(),
      });
    }
  }, [data]);

  return (
    <div
      ref={textref}
      className={`px-[24px] lg:px-4 relative flex justify-center  items-center flex-col ${getBgClasses(
        data.animationName
      )}`}>
      <article
        className={`${
          !data.isArc ? "container" : "container w-full"
        }  ${getCardClasses(data.isArc)}`}>
        <div
          className={`w-full flex flex-col items-center  gap-0 mx-auto ${
            data.isArc
              ? "md:max-w-[720px] lg:max-w-[1000px] xl:max-w-[1200px] 2xl:max-w-[1400px]"
              : ""
          }`}>
          {data.titleAnimation && (
            <h2
              className={`animation-title text-[#032d60] !leading-[1.2] text-5xl 2xl:text-6xl font-bold ${getTitleClasses(
                data.animationName
              )}`}
              dangerouslySetInnerHTML={{ __html: data.titleAnimation }}></h2>
          )}
          {data?.description && (
            <div
              className={`flex flex-col mx-auto ${
                data.isArc
                  ? "mb-[36px] md:mb-[84px] lg:w-8/12 xl:w-7/12"
                  : "mb-[56px]"
              } leading-[1.35] text-2xl md:text-lg md:leading-[1.4] text-[#001630] text-center description`}
              dangerouslySetInnerHTML={{ __html: data.description }}
            />
          )}
          <>
            <Component />
          </>
          {data.isArc && (
            <section className='flex justify-center items-center gap-10'>
              <h2 className='sr-only'>Animation Section</h2>
              <div className='flex flex-col items-center'>
                <Image
                  width={100}
                  height={100}
                  src={"/images/us-patent.svg"}
                  alt='US Patent'
                />
                <p className='!leading-[1.45] text-2xl lg:text-lg mb-[5px] font-medium text-[#001630]'>
                  US Patent
                </p>
                <p className='text-lg md:text-base text-[#001630]'>
                  US 11977, 477B2
                </p>
              </div>
              <div className='flex flex-col items-center'>
                <Image
                  width={100}
                  height={100}
                  src={"/images/ind-patent.svg"}
                  alt='Indian Patent'
                />
                <p className='!leading-[1.45] text-2xl lg:text-lg mb-[5px] font-medium text-[#001630]'>
                  Indian Patent
                </p>
                <p className='text-lg md:text-base text-[#001630]'>515306</p>
              </div>
            </section>
          )}
        </div>
      </article>
    </div>
  );
}
